import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { useToasts, ToastHost, PushToast, ScanFlash, Flash } from '../ui/primitives';
import { PackingStationProvider, usePackingStation } from '../contexts/PackingStationContext';
import LoginScreen from './LoginScreen';
import DashboardScreen from './DashboardScreen';
import OrdersScreen from './OrdersScreen';
import SessionScreen, { ActiveSession } from './SessionScreen';
import DoneScreen from './DoneScreen';
import PrintsScreen from './PrintsScreen';
import MagazynScreen from './MagazynScreen';
import VirtualKeyboard from './VirtualKeyboard';
import { API_URL } from '../config/api';
import { PrintProvider, usePrint } from './PrintProgress';

const THEME_KEY = 'packflow.kiosk_theme';

type KioskCtx = {
  push: PushToast;
  flash: (type: 'ok' | 'err', msg: string, sub?: string) => void;
  theme: string;
  setTheme: (t: string) => void;
  online: boolean;
  compact: boolean;
  session: ActiveSession | null;
  setSession: (s: ActiveSession | null) => void;
};

const Ctx = createContext<KioskCtx | null>(null);
export const useKiosk = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useKiosk must be inside StationApp');
  return ctx;
};

function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

function useCompact(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 760px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const on = (e: MediaQueryListEvent) => setCompact(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return compact;
}

export function StatusBar() {
  const { station, user } = usePackingStation();
  const { online } = useKiosk();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(id);
  }, []);
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const appVersion = (typeof window !== 'undefined' && localStorage.getItem('packflow.app_version'))
    || process.env.REACT_APP_VERSION || '';
  return (
    <div className="statusbar">
      <span style={{ fontWeight: 600, color: 'var(--fg-2)' }}>{station?.id || '—'}</span>
      <span>· {station?.location || station?.name || ''}</span>
      <span className={`net ${online ? '' : 'off'}`}>
        <Icon name={online ? 'wifi' : 'wifi-off'} size={13} />
        {online ? 'online' : 'offline'}
      </span>
      {user && <span className="muted">· {user.name}</span>}
      {appVersion && <span className="muted" title="Wersja aplikacji">· v{appVersion}</span>}
      <span className="clock">{hh}:{mm}</span>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const isAuthenticated = localStorage.getItem('packingStationAuth') === 'true';
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/packing-station/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function KioskBody() {
  const location = useLocation();
  const { online } = useKiosk();
  const isLogin = location.pathname.endsWith('/login');
  const isSession = location.pathname.endsWith('/session');
  const showBar = !isLogin && !isSession;
  useAutoLogout();

  return (
    <>
      {showBar && <StatusBar />}
      {!online && !isLogin && (
        <div className="offline-banner">
          <Icon name="cloud-off" size={15} /> Tryb offline — akcje są kolejkowane i zsynchronizują się po odzyskaniu połączenia.
        </div>
      )}
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/dashboard" element={<Protected><DashboardScreen /></Protected>} />
        <Route path="/orders" element={<Protected><OrdersScreen /></Protected>} />
        <Route path="/session" element={<Protected><SessionScreen /></Protected>} />
        <Route path="/done" element={<Protected><DoneScreen /></Protected>} />
        <Route path="/prints" element={<Protected><PrintsScreen /></Protected>} />
        <Route path="/magazyn" element={<Protected><MagazynScreen /></Protected>} />
        <Route path="/" element={<Navigate to="/packing-station/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/packing-station/dashboard" replace />} />
      </Routes>
    </>
  );
}

/* Wskaznik grajacego radia (kiosk, prawy dolny rog) */
function RadioIndicator() {
  const { radioPlaying, radioStation, radioMuted, setRadioMuted } = usePackingStation();
  if (!radioStation || !radioPlaying) return null;
  return (
    <div style={{ position: 'fixed', bottom: 18, right: 18, zIndex: 140, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '8px 12px', boxShadow: 'var(--shadow-2)' }}>
      <Icon name="radio" size={16} style={{ color: 'var(--accent-text)' }} />
      <span style={{ fontSize: 13, fontWeight: 600 }}>{radioStation.name}</span>
      <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => setRadioMuted(!radioMuted)}>
        <Icon name={radioMuted ? 'volume-x' : 'volume-2'} size={15} />
      </button>
    </div>
  );
}

/* Ustawienia → Panel pakowania (GET /api/settings/station). */
type StationConfig = { unidentifiedFirst: boolean; autoLogoutEnabled: boolean; autoLogoutMinutes: number };

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/* Auto-wylogowanie operatora po N minutach bezczynności (włączane przez admina).
   Aktywność = dotyk, klik, klawiatura (także skaner, który pisze jak klawiatura), przewijanie.
   Nigdy w trakcie sesji pakowania: operator, który odszedł od paczki na dłużej, ma do niej
   wrócić, a nie logować się i odzyskiwać sesję. Ani przy otwartym ekranie drukowania: czeka na
   potwierdzenie wydruku (i kontrolę adresu), a nakładka zostaje nad każdym ekranem, więc
   zostałaby nad logowaniem. Licznik rusza od nowa po każdej zmianie ekranu i po zamknięciu
   ekranu drukowania, więc wyjście z długiej sesji nie wylogowuje od razu. Wstrzymane też, gdy
   ekran ustawił flagę zajętości `window.__packflowBusy` (np. niezapisana dostawa w Magazynie) —
   ta sama flaga wstrzymuje przeładowanie nowej wersji (config/versionWatcher.ts).
   Wołany w KioskBody — musi być wewnątrz PrintProvider. */
function useAutoLogout() {
  const { user, logout } = usePackingStation();
  const { push } = useKiosk();
  const { printing } = usePrint();
  const navigate = useNavigate();
  const location = useLocation();
  const paused = location.pathname.endsWith('/session') || printing;
  const lastActivity = useRef(Date.now());

  const { data: cfg } = useQuery<StationConfig>({
    queryKey: ['station-config'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/station`)).data,
    enabled: !!user,
    refetchInterval: 5 * 60_000,
  });
  const timeoutMs = cfg?.autoLogoutEnabled ? Math.max(1, cfg.autoLogoutMinutes || 0) * 60_000 : 0;
  // `logout` z kontekstu nie jest memoizowany — przez ref, żeby licznik nie startował od nowa
  // przy każdym renderze providera (np. zmiana głośności radia).
  const expire = useRef<() => void>(() => undefined);
  expire.current = () => {
    void logout();
    navigate('/packing-station/login');
    push(`Wylogowano automatycznie po ${Math.round(timeoutMs / 60_000)} min bezczynności`, 'info');
  };

  useEffect(() => {
    lastActivity.current = Date.now();
  }, [user?.id, location.pathname, printing]);

  useEffect(() => {
    if (!user || !timeoutMs) return;
    const touch = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { capture: true, passive: true }));
    const id = setInterval(() => {
      if (paused || window.__packflowBusy) { lastActivity.current = Date.now(); return; }
      if (Date.now() - lastActivity.current < timeoutMs) return;
      clearInterval(id);
      expire.current();
    }, 10_000);
    return () => {
      clearInterval(id);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
    };
  }, [user, timeoutMs, paused]);
}

function StationAppInner() {
  const { station } = usePackingStation();
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  const [toasts, push] = useToasts();
  const [flashState, setFlashState] = useState<Flash>(null);
  const [session, setSession] = useState<ActiveSession | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const online = useOnline();
  const compact = useCompact();
  const navigate = useNavigate();
  const location = useLocation();

  const setTheme = useCallback((t: string) => {
    setThemeState(t);
    localStorage.setItem(THEME_KEY, t);
  }, []);

  const flash = useCallback((type: 'ok' | 'err', msg: string, sub?: string) => {
    setFlashState({ type, msg, sub });
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashState(null), 700);
  }, []);

  // 401/wylogowanie -> wroc do logowania PIN
  useEffect(() => {
    const onExpired = () => navigate('/packing-station/login');
    window.addEventListener('packflow:auth-expired', onExpired);
    return () => window.removeEventListener('packflow:auth-expired', onExpired);
  }, [navigate]);

  // Live refresh: po każdej zmianie widoku daj szansę dokończyć odłożone przeładowanie
  // (np. po wyjściu z sesji pakowania), gdy w tle pojawiła się nowa wersja aplikacji.
  useEffect(() => {
    window.dispatchEvent(new Event('packflow:route-changed'));
  }, [location.pathname]);

  // Per-station kiosk scaling: `ui_scale` zooms the whole interface (great for big
  // screens — admin sets e.g. 150% per station), `text_scale` bumps text on top.
  // `zoom` (not transform) so fixed-position sheets/dock scale correctly in Chromium.
  // Applies after login (station carries the values); changing it takes effect on the
  // station's next login.
  const uiScale = Math.min(300, Math.max(50, station?.ui_scale ?? 100)) / 100;
  const textScale = Math.min(300, Math.max(50, station?.text_scale ?? 100));

  return (
    <Ctx.Provider value={{ push, flash, theme, setTheme, online, compact, session, setSession }}>
      <div
        className="kiosk"
        data-theme={theme}
        style={{ zoom: uiScale, fontSize: textScale === 100 ? undefined : `${textScale}%` }}
      >
        {/* Ekran drukowania wspólny dla wszystkich ekranów stacji (usePrint). */}
        <PrintProvider>
          <KioskBody />
        </PrintProvider>
        <ScanFlash flash={flashState} />
        <ToastHost toasts={toasts} kiosk />
        <RadioIndicator />
        <VirtualKeyboard />
      </div>
    </Ctx.Provider>
  );
}

export default function StationApp() {
  return (
    <PackingStationProvider>
      <StationAppInner />
    </PackingStationProvider>
  );
}
